import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/config/brand";

// ─────────────────────────────────────────────────────────────────────
// PlugitAIChat — floating chat widget on the PlugitAI marketing site.
// Talks to /api/public/chat with tenant_slug="plugitai" (Kit's assistant).
// Strips [SIGNAL:*] tags before display, shows a small "sent to Kit" banner
// when BUYING_INTENT / DESIGN_BRIEF_READY signals fire.
// ─────────────────────────────────────────────────────────────────────

const C = BRAND.colors;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIGNAL_RX = /\[SIGNAL:[A-Z_]+\]/g;

function extractSignals(text) {
  const signals = [...(text.match(SIGNAL_RX) || [])].map((s) => s.replace(/\[SIGNAL:|\]/g, ""));
  const clean = text.replace(SIGNAL_RX, "").trim();
  return { signals, clean };
}

function makeSessionId() {
  return `plugitai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function PlugitAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'ai', text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(null); // 'buying' | 'brief_ready' | 'payment'
  const [sessionId] = useState(() => {
    try { return sessionStorage.getItem("plugitai_chat_sid") || (() => { const id = makeSessionId(); sessionStorage.setItem("plugitai_chat_sid", id); return id; })(); }
    catch { return makeSessionId(); }
  });
  const scrollRef = useRef(null);

  // Seed welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "ai", text: "Hey — want to hear a funny story? Tell me a little about your business first and I'll tell you mine. No forms, no pressure — just a real conversation." }]);
    }
  }, [open, messages.length]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/chat`, {
        tenant_slug: "plugitai",
        session_id: sessionId,
        message: text,
      });
      const { signals, clean } = extractSignals(String(data.reply || ""));
      setMessages((m) => [...m, { role: "ai", text: clean }]);

      if (signals.includes("DESIGN_BRIEF_READY")) setHandoff("brief_ready");
      else if (signals.includes("PAYMENT_OFFER")) setHandoff("payment");
      else if (signals.includes("BUYING_INTENT")) setHandoff((prev) => prev || "buying");

      if (window.gtag) {
        window.gtag("event", "plugitai_chat_message", { session_id: sessionId });
        signals.forEach((s) => window.gtag("event", `plugitai_chat_signal_${s.toLowerCase()}`, { session_id: sessionId }));
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "Sorry — I hit a snag on my end. Give me a moment or call Chris directly at (972) 472-8406." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handoffBanner = handoff && (
    <div
      className="px-4 py-3 border-t flex items-start gap-2 text-xs"
      style={{
        background: `linear-gradient(135deg, ${C.blueBright}18 0%, ${C.greenBright}18 100%)`,
        borderColor: `${C.blueBright}44`,
        color: C.textDim,
      }}
      data-testid="plugitai-chat-handoff-banner"
    >
      <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: C.greenGlow }} />
      <span>
        {handoff === "brief_ready" && "Your details are ready to send to Kit. He'll review your business and shape the design direction."}
        {handoff === "payment" && "Ready to lock in your $299.99 project? Chris will follow up with the secure payment link within minutes."}
        {handoff === "buying" && "Kit's been notified of your interest — he'll personally review the conversation shortly."}
      </span>
    </div>
  );

  return (
    <>
      {/* Floating trigger — bottom-LEFT (opposite music button) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-[60] bottom-4 left-4 md:bottom-8 md:left-8 flex items-center gap-2 md:gap-2.5 pl-3 md:pl-3.5 pr-3.5 md:pr-5 h-10 md:h-12 rounded-full font-semibold text-[10px] md:text-xs uppercase tracking-[0.16em] transition-all hover:-translate-y-0.5 shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
            color: "white",
            boxShadow: `0 16px 48px -12px ${C.blueBright}88`,
          }}
          data-testid="plugitai-chat-open"
          aria-label="Chat with Kit's assistant"
        >
          <MessageCircle size={15} className="shrink-0" />
          <span>Chat with Kit</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          className="fixed z-[70] bottom-4 left-4 md:bottom-6 md:left-6 w-[calc(100vw-2rem)] max-w-[400px] h-[calc(100dvh-6rem)] max-h-[640px] flex flex-col rounded-2xl overflow-hidden border shadow-2xl"
          style={{
            background: `linear-gradient(180deg, ${C.inkSoft} 0%, ${C.ink} 100%)`,
            borderColor: `${C.blueBright}55`,
            boxShadow: `0 60px 160px -30px ${C.blueBright}66`,
          }}
          data-testid="plugitai-chat-drawer"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                boxShadow: `0 0 20px ${C.blueBright}55`,
              }}
            >
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">Kit&apos;s Assistant</div>
              <div className="text-[10px] uppercase tracking-[0.2em] flex items-center gap-1.5" style={{ color: C.greenGlow }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.greenGlow, boxShadow: `0 0 8px ${C.greenGlow}` }} />
                Online · replies in seconds
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
              data-testid="plugitai-chat-close"
              aria-label="Close chat"
            >
              <X size={18} style={{ color: C.textDim }} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="plugitai-chat-messages">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))}
            {busy && <TypingDots />}
          </div>

          {handoffBanner}

          {/* Input */}
          <div className="p-3 border-t flex items-end gap-2" style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Type your question…"
              className="flex-1 resize-none rounded-xl px-3 py-2.5 text-sm outline-none border max-h-[120px]"
              style={{
                background: "rgba(255,255,255,0.04)",
                borderColor: C.line,
                color: C.text,
              }}
              data-testid="plugitai-chat-input"
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                color: "white",
                boxShadow: `0 8px 24px -8px ${C.greenBright}88`,
              }}
              data-testid="plugitai-chat-send"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`plugitai-chat-bubble-${role}`}
    >
      <div
        className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
        style={
          isUser
            ? {
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                color: "white",
                borderBottomRightRadius: 4,
              }
            : {
                background: "rgba(255,255,255,0.06)",
                color: C.text,
                border: `1px solid ${C.line}`,
                borderBottomLeftRadius: 4,
              }
        }
      >
        {text}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-3.5 py-2.5" data-testid="plugitai-chat-typing">
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          className="w-2 h-2 rounded-full animate-bounce"
          style={{ background: C.blueGlow, animationDelay: `${d}ms` }}
        />
      ))}
    </div>
  );
}
